import { getClientAbbreviation } from "./clientAbbrev.ts";

// Pinned subviews: any page, with whatever filters it carries in its URL, can
// be starred into the sidebar's Favorites section. Stored per-user in
// user_preferences on Directus (with local cache / fallback in localStorage)
// so favorites follow the user across browsers and sessions.

export type Favorite = {
  url: string;
  label: string;
  createdAt: number;
  client?: string;
  clientAbbr?: string;
  project?: string;
  pinnedToDashboard?: boolean;
};

const LS_KEY = "cordite.favorites";
export const MAX_FAVORITES = 25;
export const FAVORITES_CHANGED = "cordite:favorites-changed";

type FavoritesPersister = (favs: Favorite[]) => void;
let persister: FavoritesPersister | null = null;

export function registerFavoritesPersister(p: FavoritesPersister | null): void {
  persister = p;
}

export function loadFavorites(): Favorite[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (f): f is Favorite =>
        !!f && typeof f.url === "string" && f.url.startsWith("/") && typeof f.label === "string"
    );
  } catch {
    return [];
  }
}

export function syncFavoritesFromServer(serverFavs: unknown): Favorite[] {
  if (!Array.isArray(serverFavs)) return loadFavorites();
  const valid = serverFavs.filter(
    (f): f is Favorite =>
      !!f && typeof f.url === "string" && f.url.startsWith("/") && typeof f.label === "string"
  );
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(valid));
  } catch {
    /* ignore */
  }
  if (typeof window !== "undefined") window.dispatchEvent(new Event(FAVORITES_CHANGED));
  return valid;
}

export function clearFavoritesCache(): void {
  try {
    localStorage.removeItem(LS_KEY);
  } catch {
    /* ignore */
  }
  if (typeof window !== "undefined") window.dispatchEvent(new Event(FAVORITES_CHANGED));
}

function persist(favs: Favorite[]): Favorite[] {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(favs));
  } catch {
    // storage full or unavailable — the in-memory list is still authoritative
    // for this session's UI
  }
  if (persister) {
    try {
      persister(favs);
    } catch {
      /* ignore server error */
    }
  }
  if (typeof window !== "undefined") window.dispatchEvent(new Event(FAVORITES_CHANGED));
  return favs;
}

export function isFavorite(url: string, favs = loadFavorites()): boolean {
  return favs.some((f) => f.url === url);
}

/**
 * Checks whether a target URL (with pathname and optional search params)
 * matches the currently active route and search params.
 */
export function isUrlActive(targetUrl: string, currentPathname: string, currentSearch = ""): boolean {
  try {
    const target = new URL(targetUrl, "https://cordite.local");
    if (target.pathname !== currentPathname) return false;

    const currentParams = new URLSearchParams(currentSearch);
    let targetParamCount = 0;
    for (const [key, val] of target.searchParams.entries()) {
      if (!val) continue;
      targetParamCount++;
      if (currentParams.get(key) !== val) return false;
    }

    if (targetParamCount > 0) {
      let currentParamCount = 0;
      for (const [key, val] of currentParams.entries()) {
        if (val) currentParamCount++;
      }
      return currentParamCount === targetParamCount;
    }

    // Target has no search params: active only when current also has no active search params
    for (const val of currentParams.values()) {
      if (val) return false;
    }
    return true;
  } catch {
    return targetUrl === currentPathname + currentSearch;
  }
}

export function getFavorite(url: string, favs = loadFavorites()): Favorite | undefined {
  return favs.find((f) => f.url === url);
}

/** Save or update a favorite with a specific label. Returns the updated list. */
export function saveFavorite(
  url: string,
  label: string,
  extra?: { client?: string; clientAbbr?: string; project?: string; pinnedToDashboard?: boolean }
): Favorite[] {
  const favs = loadFavorites();
  const i = favs.findIndex((f) => f.url === url);
  const trimmed = label.trim() || "Favorite";
  const clientName = extra?.client;
  const clientAbbr = extra?.clientAbbr || (clientName ? getClientAbbreviation(clientName) : undefined);
  const pinnedToDashboard = extra?.pinnedToDashboard;

  if (i >= 0) {
    favs[i] = {
      ...favs[i],
      label: trimmed,
      ...(clientName ? { client: clientName } : {}),
      ...(clientAbbr ? { clientAbbr } : {}),
      ...(extra?.project ? { project: extra.project } : {}),
      ...(pinnedToDashboard !== undefined ? { pinnedToDashboard } : {}),
    };
    return persist(favs);
  }

  const item: Favorite = {
    url,
    label: trimmed,
    createdAt: Date.now(),
    ...(clientName ? { client: clientName } : {}),
    ...(clientAbbr ? { clientAbbr } : {}),
    ...(extra?.project ? { project: extra.project } : {}),
    ...(pinnedToDashboard ? { pinnedToDashboard: true } : {}),
  };
  const next = [item, ...favs].slice(0, MAX_FAVORITES);
  return persist(next);
}

export function togglePinToDashboard(url: string): Favorite[] {
  const favs = loadFavorites();
  const i = favs.findIndex((f) => f.url === url);
  if (i < 0) return favs;
  favs[i] = { ...favs[i], pinnedToDashboard: !favs[i].pinnedToDashboard };
  return persist(favs);
}

export function renameFavorite(url: string, newLabel: string): Favorite[] {
  const favs = loadFavorites();
  const i = favs.findIndex((f) => f.url === url);
  if (i < 0) return favs;
  const trimmed = newLabel.trim();
  if (!trimmed) return favs;
  favs[i] = { ...favs[i], label: trimmed };
  return persist(favs);
}

/** Toggle a url in/out of the list. New favorites go first; returns the list. */
export function toggleFavorite(
  url: string,
  label: string,
  extra?: { client?: string; clientAbbr?: string; project?: string }
): Favorite[] {
  const favs = loadFavorites();
  const i = favs.findIndex((f) => f.url === url);
  if (i >= 0) {
    favs.splice(i, 1);
    return persist(favs);
  }
  // When the cap is hit the oldest (last) favorite falls off the end.
  const clientName = extra?.client;
  const clientAbbr = extra?.clientAbbr || (clientName ? getClientAbbreviation(clientName) : undefined);
  const item: Favorite = {
    url,
    label,
    createdAt: Date.now(),
    ...(clientName ? { client: clientName } : {}),
    ...(clientAbbr ? { clientAbbr } : {}),
    ...(extra?.project ? { project: extra.project } : {}),
  };
  const next = [item, ...favs].slice(0, MAX_FAVORITES);
  return persist(next);
}

export function removeFavorite(url: string): Favorite[] {
  return persist(loadFavorites().filter((f) => f.url !== url));
}

export function reorderFavorites(fromIndex: number, toIndex: number): Favorite[] {
  const favs = loadFavorites();
  if (fromIndex < 0 || fromIndex >= favs.length || toIndex < 0 || toIndex >= favs.length || fromIndex === toIndex) {
    return favs;
  }
  const item = favs.splice(fromIndex, 1)[0];
  favs.splice(toIndex, 0, item);
  return persist(favs);
}

export function moveFavorite(url: string, direction: "up" | "down"): Favorite[] {
  const favs = loadFavorites();
  const i = favs.findIndex((f) => f.url === url);
  if (i < 0) return favs;
  const target = direction === "up" ? i - 1 : i + 1;
  return reorderFavorites(i, target);
}

/**
 * Drops client/project favorites the user cannot read. A favorite is a saved
 * link plus a label, so without this a pin created under another account (or
 * before access was narrowed) keeps showing a name the user has no access to.
 */
export function visibleFavorites(favs: Favorite[], clientIds: ReadonlySet<string>, projectIds: ReadonlySet<string>): Favorite[] {
  return favs.filter((f) => {
    const m = f.url.match(/^\/(clients|projects)\/([^/?#]+)/);
    if (!m) return true;
    return (m[1] === "clients" ? clientIds : projectIds).has(m[2]);
  });
}

export type OrganizedFavorite = {
  favorite: Favorite;
  isIndented?: boolean;
  parentClient?: string;
};

export function organizeFavorites(favs: Favorite[]): OrganizedFavorite[] {
  if (favs.length === 0) return [];

  const clientMap = new Map<string, Favorite>();
  for (const f of favs) {
    if (f.url.startsWith("/clients/")) {
      clientMap.set(f.label.trim().toLowerCase(), f);
      const match = f.url.match(/^\/clients\/([^/?#]+)/);
      if (match) clientMap.set(match[1], f);
    }
  }

  if (clientMap.size === 0) {
    return favs.map((f) => ({ favorite: f, isIndented: false }));
  }

  const projectChildren = new Map<string, Favorite[]>();
  const topLevelFavs: Favorite[] = [];

  for (const f of favs) {
    const clientKey = (f.client || "").trim().toLowerCase();
    const isClient = f.url.startsWith("/clients/");
    if (!isClient && clientKey && clientMap.has(clientKey)) {
      const parentFav = clientMap.get(clientKey)!;
      const list = projectChildren.get(parentFav.url) ?? [];
      list.push(f);
      projectChildren.set(parentFav.url, list);
    } else {
      topLevelFavs.push(f);
    }
  }

  const result: OrganizedFavorite[] = [];
  for (const f of topLevelFavs) {
    result.push({ favorite: f, isIndented: false });
    const children = projectChildren.get(f.url);
    if (children && children.length > 0) {
      for (const child of children) {
        result.push({ favorite: child, isIndented: true, parentClient: f.label });
      }
    }
  }

  return result;
}

/** Sidebar label for a favorite: page title plus a hint of the filter state. */
export function favoriteLabel(title: string, search: string): string {
  const sp = new URLSearchParams(search);
  const q = sp.get("q");
  if (q) return `${title} · “${q}”`;
  if ([...sp.keys()].length > 0) return `${title} · filtered`;
  return title;
}
